import { atom } from "jotai";
import { JotaiTool } from "../../../../index";

// V2 Field Mapping Tools
import { getAppNameAtom } from "../../../zap_field_mapping/get_app_name";
import { getAppActionsAtom } from "../../../zap_field_mapping/get_app_actions";
import { getAppNeedsAtom } from "../../../zap_field_mapping/get_app_needs";
import { getAppAuthAtom } from "../../../zap_field_mapping/get_app_auth";
import { getAppChoicesAtom } from "../../../zap_field_mapping/get_app_choices";

// Authentication tools for direct auth creation
import { createAuthAtom } from "../../../create_auth";
import { confirmAuthAtom } from "../../../confirm_auth";
import { authConfirmationButtonAtom } from "../../../auth_confirmation_button";

// Shared tools for Zap operations
import { getZapAtom } from "../../../get_zap";
import { getTableByIdAtom } from "../../../get_table_by_id";
import { updateZapAtom } from "../../../update_zap";
import { validateZslAtom } from "../../../validate_zsl";

export const zapOperationsToolsAtom = atom<JotaiTool<any>[]>((get) => [
  // V2 Hierarchical Field Mapping Tools
  get(getAppNameAtom),
  get(getAppActionsAtom),
  get(getAppNeedsAtom),
  get(getAppAuthAtom),
  get(getAppChoicesAtom),

  // Authentication tools for direct auth creation
  get(createAuthAtom),
  get(confirmAuthAtom),
  get(authConfirmationButtonAtom),

  // Core Zap operations tools
  get(getZapAtom),
  get(getTableByIdAtom),
  get(updateZapAtom),
  get(validateZslAtom),
]);
zapOperationsToolsAtom.debugLabel = "zapOperationsToolsAtom";

export const zapOperationsRecipe = `## 🚀 Zapier Operations - Complete Workflow Management

## 🚨 CRITICAL: ZAPIER INTERNAL APPS NEVER NEED AUTH 🚨

**BLOCKING RULE - CHECK FIRST:**
Before calling get_app_auth, identify if the app is a Zapier internal app:
- FilterAPI, FormatterCLIAPI, DelayAPI, DigestCLIAPI, EmailCLIAPI, RSSCLIAPI
- WebhooksCLIAPI, StorageCLIAPI, ScheduleCLIAPI, PathsCLIAPI, AI by Zapier
- **TablesAPI, TablesCLIAPI, InterfacesAPI, InterfacesCLIAPI, ChatbotsAPI, ChatbotsCLIAPI**
- Any app with "CLIAPI" or "API" suffix that's made by Zapier

**❌ FORBIDDEN: NEVER call get_app_auth for Zapier internal apps**
**✅ REQUIRED: Skip directly to get_app_choices for Zapier internal apps**

## ⚠️ MANDATORY: APP LOOKUP BEFORE ANY CONFIGURATION ⚠️

**STEP 0: ALWAYS START WITH get_app_name**
Before ANY Zap operation (creation, modification, configuration):
1. Call get_app_name for EVERY app in the Zap
2. Store the exact implementation_id returned
3. Use that implementation_id for all subsequent operations

**Example Workflow:**
- User says "add a filter step"
- ❌ WRONG: Assume it's "FilterCLIAPI" 
- ✅ RIGHT: get_app_name("filter") → returns "FilterAPI" → use "FilterAPI"

**NEVER GUESS APP IDENTIFIERS:**
- With 8000+ apps, guessing causes "deleted app" errors
- Every app has a unique identifier that must be looked up
- Even common apps like Filter, Formatter need lookup

## 🚨 CRITICAL: MANDATORY EXECUTION ORDER 🚨

**BLOCKING RULE: Complete Phase 1 FULLY before Phase 2:**
- ❌ NEVER start authentication until COMPLETE skeleton exists
- ❌ NEVER start field mapping until ALL steps are created and authenticated
- ✅ Build ALL skeleton steps → Handle ALL authentication → Then field mapping

**CHECKPOINT VALIDATION:**
Before proceeding to next phase, verify:
1. "Do I have ALL required steps in the skeleton?" 
2. "Have I counted steps needed vs steps created?"
3. "Is the skeleton 100% complete before I start auth?"

## COMPLETE ZAP WORKFLOW PHASES

### Phase 1: Complete Skeleton Creation (MUST FINISH FIRST)

**🚨 BLOCKING: Build COMPLETE skeleton before ANY auth/field mapping:**
1. **MANDATORY: Use get_app_name** for EVERY app referenced
2. **COUNT STEPS**: Determine how many steps the user needs TOTAL
3. Create ALL steps in the skeleton - trigger + ALL actions
4. **CHECKPOINT**: Verify skeleton has ALL required steps before proceeding

**For NEW Zap workflows:**
1. **MANDATORY: Use get_app_name** for EVERY app referenced
2. Query implementations API to get action details:
   \`\`\`
   network_request({
     method: "GET",
     api: "platform",
     path: "/v4/implementations?selected_apis=\${implementation_id}"
   })
   \`\`\`
3. Create skeleton structure using CORRECT endpoint:
   \`\`\`
   network_request({
     method: "POST",
     api: "gulliver",
     path: "/storage/v1/zaps",
     body: {
       "id": "_GEN_ROOT",
       "zdl": {
         "id": "_GEN_ROOT",
         "app": "EngineAPI",
         "type": "run",
         "action": "series_skip_errors",
         "timezone": "America/New_York",
         "zdl_version": "0.4",
         "title": "User's Zap Title",
         "steps": [
           {
             "id": "_GEN_1",
             "app": "\${implementation_id}",
             "type": "read",
             "action": "\${action_key}",
             "params": {},
             "meta": {}
           }
         ]
       }
     }
   })
   \`\`\`

**❌ WRONG ENDPOINTS (404 ERROR):**
- path: "/v4/zaps"
- api: "zsl-api"

### Phase 2: Authentication Setup (ONLY AFTER SKELETON COMPLETE)

**⚠️ PREREQUISITE: Complete skeleton from Phase 1 MUST exist first ⚠️**

**BLOCKING CHECKPOINT**: Before starting authentication:
1. Verify skeleton has ALL required steps
2. Count total steps needed vs steps created
3. If skeleton incomplete → Return to Phase 1

**🚨 ZAPIER INTERNAL APPS - SKIP AUTHENTICATION (CRITICAL REMINDER):**

**EXACT APP IDS TO SKIP AUTH FOR:**
- FilterAPI, FilterCLIAPI (Filter by Zapier)
- FormatterCLIAPI (Formatter by Zapier)  
- DelayAPI (Delay by Zapier)
- DigestCLIAPI (Digest by Zapier)
- EmailCLIAPI (Email by Zapier)
- RSSCLIAPI (RSS by Zapier)
- WebhooksCLIAPI (Webhooks by Zapier)
- StorageCLIAPI (Storage by Zapier)
- ScheduleCLIAPI (Schedule by Zapier)
- PathsCLIAPI, BranchingAPI (Paths by Zapier)
- App197505CLIAPI (AI by Zapier)
- **InterfacesAPI, InterfacesCLIAPI (Interfaces by Zapier)**
- **TablesAPI, TablesCLIAPI (Tables by Zapier)**
- **ChatbotsAPI, ChatbotsCLIAPI (Chatbots by Zapier)**

**BLOCKING ENFORCEMENT:**
- ❌ If implementation_id matches ANY of the above → DO NOT call get_app_auth
- ❌ If app name contains "by Zapier" → DO NOT call get_app_auth
- ✅ Skip directly to get_app_choices for field mapping
- ✅ These apps work without authentication

**Authentication Flow (MANDATORY FOR NON-ZAPIER APPS ONLY):**

1. **Use get_app_auth** to check authentication status for EACH step needing auth
   - This tool enforces user confirmation protocol
   - NEVER use network_request directly to /authentications

2. **Handle Authentication Responses:**
   - \`needs_new_auth: true\` → Use create_auth immediately
   - After create_auth succeeds → **MANDATORY: Use confirm_auth** to apply to steps
   - \`REQUIRES_USER_INPUT: true\` → Stop and wait for user input
   - \`BLOCKS_FIELD_MAPPING: true\` → Stop until user confirms
   - \`authentication_status: "not_supported"\` → Skip auth, continue with field mapping

3. **NEW: After Authentication Creation:**
   - create_auth auto-sends confirm_auth message
   - confirm_auth will be called automatically by the AI
   - **DO NOT ask for confirmation** - user already confirmed by creating it
   - Proceed directly to field mapping after confirm_auth succeeds

4. **Auth Confirmation Protocol (ONLY FOR EXISTING AUTH):**
   - **IF** get_app_auth found EXISTING authentication:
     - Single auth found → Use auth_confirmation_button with scenario="single_auth"
     - Multiple auths found → Use auth_confirmation_button with scenario="multiple_auth"
   - **IF** get_app_auth said needs_new_auth → create_auth then auto-confirm (NO button needed)
   - **CRITICAL**: Never use continue_button for auth confirmation - creates UX conflicts
   - Wait for button response before proceeding ONLY for existing auth

**User confirmation is required for EXISTING auth, but NOT for newly created auth** - creating is the confirmation.

### Phase 3: Field Mapping & Configuration

**Field Mapping V2 Hierarchical Flow:**
1. **get_app_actions** → Discover available actions
2. **get_app_needs** → Get field requirements for chosen action
3. **get_app_choices** → Get field options and configure parameters

**Note:** Authentication is handled in Phase 2 - do NOT call get_app_auth again in Phase 3

**Field Mapping Features:**
- **Multi-layer Validation**: Parameters validated against needs, types, and structure
- **Auto-Authentication**: Uses confirmed authentication from Phase 2
- **Type Safety**: Validates string, number, boolean, and choice fields automatically
- **Smart Error Prevention**: Catches field mapping errors before they reach Zapier

**🚨 FIELD MAPPING INTEGRITY 🚨**

**When you don't have actual field data:**
- For dynamic/external data (RSS, webhooks, APIs): "I don't have the actual field structure for this [source] yet. Let's test this step together to see what data is available, then I can help you map the fields accurately."
- For user-specific data (channels, IDs): Ask the user directly for the specific values: "Which [channel/folder/ID] would you like to use here? I want to make sure I configure this correctly."
- NEVER guess or hallucinate field names, values, or structure
- Be transparent: "I don't want to guess here - let's make sure we get this right"
- Be friendly and collaborative: "Let's figure this out together!"

**CRITICAL: When asking these questions → NO continue_button**
- Self-check: "Did I just ask a question?" If YES → Wait for user input, NO BUTTONS
- Questions require answers, not continuation buttons

### Phase 4: Validation & Testing

**Use update_zap** to save configured Zap with all parameters:
- Single comprehensive update with complete steps array
- Built-in validation ensures parameters, structure, and configuration are correct

**Additional Validation:**
- **validate_zsl**: Validate Zap structure and syntax
- **get_zap**: Retrieve and inspect existing Zap workflows

## 🔴 CRITICAL BLOCKING CONDITIONS

**Authentication Enforcement:**
- \`needs_new_auth: true\` → STOP, use create_auth
- \`authentication_not_supported: true\` → Skip auth, proceed to get_app_choices
- \`REQUIRES_USER_INPUT: true\` → STOP, wait for user input
- \`BLOCKS_FIELD_MAPPING: true\` → STOP, confirmation required
- User has not explicitly confirmed auth selection → STOP, ask user

**Before get_app_choices or update_zap, verify:**
1. "Did I call get_app_auth and receive a response?"
2. "Did the user explicitly confirm which authentication to use?"
3. "Am I about to skip authentication verification?" (Answer must be NO)
4. "Did I wait for user input when REQUIRES_USER_INPUT was true?"

**If ANY answer is wrong, STOP and fix the authentication flow first.**

## ERROR RECOVERY & PERSISTENCE

**Developer Mode Error Handling:**
- Show exact error messages with tool names and parameters used
- Display HTTP status codes and response bodies when available
- Ask for specific help: "🔧 API FAILURE: Got [status code] from [endpoint]. Parameters used: [params]. What should I try next?"
- Suggest alternatives: "🔧 TOOL ERROR: [tool name] failed with [error]. Should I try [different approach] instead?"
- Request guidance: "🔧 MISSING INFO: I need [specific data/schema] to configure [feature]. Could you provide it?"

**When API Calls Fail:**
- Try minimum 3 different approaches before asking for help
- In developer mode: Show what was attempted after each failure
- 404/405 errors: Ask "🔧 Got [status] on [endpoint]. Is this the right path? Should I try [alternative]?"
- Use correct endpoints:
  - Zap creation: POST /storage/v1/zaps on gulliver API
  - App search: GET /v4/implementations-meta/search?term=<search_term>
  - Implementation details: GET /v4/implementations?selected_apis={id}

**Search Persistence:**
- Try minimum 3 different search terms for apps
- In developer mode: Show search attempts: "🔧 Tried searching for: [term1], [term2], [term3]. Should I try different terms?"
- Most operations CAN be accomplished through available tools

## V2 Performance Features

**Caching and Optimization:**
- get_app_choices results automatically paginated and cached
- Parallel processing for multiple operations
- Smart error prevention catches issues early

**REMEMBER:** This recipe provides complete Zap workflow management from creation to deployment. Always use the hierarchical tool sequence and enforce authentication protocols.`;
